import React, { useEffect, useState } from 'react';
import { Button, Tag, Typography, message } from 'antd';
import {
	ClockCircleOutlined,
	PlusOutlined,
	SolutionOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ContentModal from './ContentModal';
import PanelFrame from './PanelFrame';
import ReviewFormModal from './ReviewFormModal';
import ReviewIssuesModal from './ReviewIssuesModal';
import ReviewsTable from './ReviewsTable';
import { authorName, isReviewAuthor } from './authors';
import { useContentViewer } from './useContentViewer';
import type { PanelChrome } from './layout';
import {
	useDeleteProgramReviewMutation,
	useGetProgramReviewsQuery,
} from '../../redux/api/manufacturing';
import { useGetLoggedInUserQuery } from '../../redux/api/users';
import type { ProgramReview } from '../../interfaces/CamProgramming.interface';

const { Text } = Typography;

interface ReviewsPanelProps {
	programId: string;
	/**
	 * Whether the review can be attributed to someone other than its author.
	 * With this off the server credits the signed-in user, which is what the
	 * order line item's scoring section wants.
	 */
	allowReviewerOverride?: boolean;
	chrome?: PanelChrome;
}

const ReviewsPanel: React.FC<ReviewsPanelProps> = ({
	programId,
	allowReviewerOverride = true,
	chrome,
}) => {
	const { data, isLoading } = useGetProgramReviewsQuery({
		program: programId,
	});
	const { data: currentUser } = useGetLoggedInUserQuery();
	const [deleteReview] = useDeleteProgramReviewMutation();

	const [modalOpen, setModalOpen] = useState(false);
	const [editingReview, setEditingReview] = useState<ProgramReview | null>(null);
	const viewer = useContentViewer<ProgramReview>();
	const issuesViewer = useContentViewer<ProgramReview>();

	const reviews = data?.results || [];

	useEffect(() => {
		setModalOpen(false);
		setEditingReview(null);
	}, [programId]);

	// A review is attributed to whoever wrote it, so only they may revise it.
	const isAuthor = (review: ProgramReview) =>
		isReviewAuthor(review, currentUser?.id);

	const handleOpenCreate = () => {
		setEditingReview(null);
		setModalOpen(true);
	};

	const handleOpenEdit = (review: ProgramReview) => {
		setEditingReview(review);
		setModalOpen(true);
	};

	const handleDelete = async (review: ProgramReview) => {
		try {
			await deleteReview({ id: review.id, programId }).unwrap();
			message.success('Review removed');
		} catch {
			message.error('Failed to remove review');
		}
	};

	return (
		<>
			<PanelFrame
				chrome={chrome}
				icon={<SolutionOutlined className="cam-section__icon" />}
				title="Reviews"
				count={reviews.length}
				actionPlacement="below"
				action={(expand) => (
					<Button
						icon={<PlusOutlined />}
						onClick={() => {
							expand();
							handleOpenCreate();
						}}
					>
						Log Review
					</Button>
				)}
			>
				<ReviewsTable
					reviews={reviews}
					loading={isLoading}
					canEdit={isAuthor}
					onView={viewer.view}
					onViewIssues={issuesViewer.view}
					onEdit={handleOpenEdit}
					onDelete={handleDelete}
				/>
			</PanelFrame>

			<ReviewFormModal
				programId={programId}
				review={editingReview}
				open={modalOpen}
				allowReviewerOverride={allowReviewerOverride}
				onClose={() => setModalOpen(false)}
			/>

			<ContentModal
				title={`Review by ${authorName(
					viewer.entry?.reviewer ?? null,
					'Unassigned'
				)}`}
				meta={
					viewer.entry && (
						<>
							{viewer.entry.review_duration_minutes != null && (
								<Text type="secondary" className="cam-entry__chip">
									<ClockCircleOutlined />{' '}
									{viewer.entry.review_duration_minutes} min
								</Text>
							)}
							{viewer.entry.review_completed_at && (
								<Text type="secondary">
									Completed{' '}
									{dayjs(viewer.entry.review_completed_at).format(
										'MMM D, YYYY [at] h:mm A'
									)}
								</Text>
							)}
						</>
					)
				}
				html={viewer.entry?.feedback ?? null}
				emptyText="No feedback"
				open={viewer.open}
				onClose={viewer.close}
				afterClose={viewer.afterClose}
			/>

			<ReviewIssuesModal
				programId={programId}
				review={issuesViewer.entry}
				open={issuesViewer.open}
				onClose={issuesViewer.close}
				afterClose={issuesViewer.afterClose}
			/>
		</>
	);
};

export default ReviewsPanel;
